import {Injectable} from '@angular/core';
import {getAPI, Scorm, ScormParamsConstants, ScormState, UserData} from '@transparent/coretypes';
import {
    AAID,
    ActivityReport,
    AssessmentResult,
    CompleteItReport,
    ContentBrowse,
    ICWApi,
    LanguageSelector,
    ListRepository,
    MlnReport,
    UUID
} from '@transparent/cw-api';


@Injectable()
export class MoneCommunicationService {
    isCWAPI: boolean;
    version: string;
    cwapi: ICWApi;

    constructor() {
        this.init();
    }

    initializeAPI(): void {
        this.init();
    }

    private init(): void {
        try {
            this.cwapi = window.CWAPI = window.CWAPI || window.parent.CWAPI;
        } catch (e) {
        }
        if (this.cwapi) {
            this.isCWAPI = true;
        } else {
            const api = getAPI();
            if (api) {
                this.version = api.version;
                if (this.version === '1.2' || typeof api['LMSInitialize'] === 'function') {
                    Scorm.lmsPrefix = 'LMS';
                } else if (this.version === '2004') {
                    Scorm.lmsPrefix = '';
                }
                const userProfile = api.getExtension(ScormState.USER_PROFILE);
                const userData = api.getExtension(ScormState.USER_DATA);
                const contentBrowse = api.getExtension(ScormState.CONTENT_BROWSER);
                const learningPath = api.getExtension(ScormState.LEARNING_PATH);
                const languages = api.getExtension(ScormState.LANGUAGES);
                const settings = api.getExtension(ScormState.SETTINGS);
                const listRepo = api.getExtension(ScormState.LIST_REPOSITORY);
                this.cwapi = {
                    main: {
                        setFocusOnMone: (isShiftTab: boolean) => {
                            if (api?.setFocusOnMone) {
                                api.setFocusOnMone(isShiftTab);
                            }
                        },
                        setCallback: (cb: (name: string, msgData?: any) => void) => {
                            if (api?.setCallback) {
                                api.setCallback(cb);
                            }
                        },
                        goHome: (options?: string) => {
                            if (api?.goHome) {
                                api.goHome(options);
                            }
                        },
                        setActivity: (idx) => {
                            if (api?.setActivity) {
                                return api.setActivity(idx);
                            }
                            return Promise.resolve();
                        }
                    },
                    altAnswer: api.getExtension(ScormState.ALTANSWER),
                    list: {
                        getActivityTemplate(listId: string): Promise<ListRepository.ActivityTemplate> {
                            return api.getActivityTemplate(listId);
                        },
                        getList(id: string): Promise<ListRepository.List> {
                            return listRepo.getList(id);
                        },
                        getLists(ids: AAID[]): Promise<ListRepository.List[]> {
                            return listRepo.getLists(ids);
                        },
                        getMediaElementResolver(list?: ListRepository.List): Promise<ListRepository.MediaResolver> {
                            if (listRepo?.getMediaElementResolver) {
                                return listRepo.getMediaElementResolver(list);
                            }
                            return Promise.resolve((path: string) => path);
                        }
                    },
                    mli: api.getExtension(ScormState.MLI),
                    contentBrowse: {
                        addSuggestionToPath(suggestion: ContentBrowse.Suggestion,
                                            languageSelector: LanguageSelector): Promise<ContentBrowse.Lesson[]> {
                            if (contentBrowse?.addSuggestionToPath) {
                                return contentBrowse.addSuggestionToPath(suggestion, languageSelector);
                            }
                            return Promise.resolve([]);
                        },
                        getSuggestionStatuses(suggestions: ContentBrowse.Suggestion[]): Promise<ContentBrowse.Suggestion[]> {
                            if (contentBrowse?.getSuggestionStatuses) {
                                return contentBrowse.getSuggestionStatuses(suggestions);
                            }
                            return Promise.resolve([]);
                        },
                        getShareableLink(lessonId?: string, activity?: number): Promise<string> {
                            let lessonType: string;
                            if (lessonId && !lessonId.includes('.')) {
                                lessonType = 'lesson';
                            }
                            if (api?.getShareableLink) {
                                return api.getShareableLink(lessonType, lessonId, activity);
                            }
                            return Promise.resolve('');
                        },
                        removeLessonsFromPath(lessons: ContentBrowse.Lesson[], selector: LanguageSelector): Promise<void> {
                            if (learningPath?.removeLessons) {
                                return learningPath.removeLessons(lessons, selector);
                            }
                            return Promise.resolve();
                        }
                    },
                    handsFree: api.getExtension(ScormState.HANDS_FREE_PLAYER),
                    languages: {
                        getAlphabetURL(selector: LanguageSelector): Promise<string> {
                            if (languages?.getAlphabetURL) {
                                return languages.getAlphabetURL(selector).then((res: any) => {
                                    return res.alphabet;
                                });
                            }
                            return Promise.resolve('');
                        },
                        hasAlphabet(selector: LanguageSelector): Promise<boolean> {
                            return settings?.getUIConfigForLanguage ?
                                settings.getUIConfigForLanguage(selector).then(obj => obj.hasAlphabet) :
                                Promise.resolve(false);
                        }
                    },
                    lesson: null,
                    platform: api.getExtension(ScormState.PLATFORM),
                    eventTracker: api.getExtension(ScormState.EVENT_TRACKER),
                    report: {
                        saveMlnReport(report: MlnReport): Promise<void> {
                            Scorm.callScormMethod('SetValue', ScormParamsConstants.MLN_REPORT, JSON.stringify(report));
                            return Promise.resolve();
                        },
                        saveActivityReport(report: ActivityReport): Promise<void> {
                            Scorm.callScormMethod('SetValue', ScormParamsConstants.ACTIVITY_REPORT, JSON.stringify(report));
                            return Promise.resolve();
                        },
                        saveCompleteItReport(report: CompleteItReport): Promise<void> {
                            Scorm.callScormMethod('SetValue', ScormParamsConstants.COMPLETE_IT_REPORT, JSON.stringify(report));
                            return Promise.resolve();
                        },
                        saveAssessmentResult(gradeBook: AssessmentResult): Promise<void> {
                            Scorm.callScormMethod('SetValue', ScormParamsConstants.LAUNCHED_DATA, JSON.stringify(gradeBook));
                            return Promise.resolve();
                        }
                    },
                    user: {
                        getUserUuid(): Promise<UUID> {
                            if (userData?.getProfile) {
                                return userProfile.getProfile().then(profile => profile.uuid);
                            }
                            return Promise.resolve('');
                        },
                        getUserData(): Promise<UserData> {
                            if (userData?.getUserData) {
                                return userData.getUserData();
                            }
                            return Promise.resolve({});
                        },
                        saveUserData(data: UserData): Promise<void> {
                            if (userData?.setUserData) {
                                return userData.setUserData(JSON.stringify(data));
                            }
                            return Promise.resolve();
                        }
                    },
                    soundRecorder: api.getExtension(ScormState.SOUND_RECORDER)
                };
                window.CWAPI = this.cwapi;
            }
        }
    }
}
